import React, { useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import Navigation from "../../../components/Navigation";
import geocoderIcon from "../../../assets/images/geocoderIcon.svg";
import wordpressIcon from "../../../assets/images/wordpress.svg";
import hubspotIcon from "../../../assets/images/hubspot.svg";
import drupalIcon from "../../../assets/images/drupalIcon.svg";
import rocketIcon from "../../../assets/images/rocketIcon.svg";
import infoIcon from "../../../assets/images/infoIcon.svg";
import primaryIcon from "../../../assets/images/primaryIcon.svg";
import Fancybox from "../EditProject/Fancybox";
import downloadIcon from "../../../assets/images/download.svg";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getProjectById } from "../../../utils/api";
import Tooltip from "../../../components/Tooltip/Tooltip";

const useProjectStore = create(
  devtools((set) => ({
    projectData: null,
    loading: false,
    error: null,

    // Action to fetch project by ID
    fetchProjectById: async ({ accessToken, id }) => {
      set({ loading: true, error: null });

      try {
        const response = await getProjectById(accessToken, id);

        set({ projectData: response?.data?.data[0], loading: false });
        console.log(response.data.data);
      } catch (error) {
        set({
          error:
            error.response?.data?.message || "Failed to fetch project data",
          loading: false,
        });
      }
    },
  })),
);

const projectTypes = {
  Geocoder: geocoderIcon,
  WordPress: wordpressIcon,
  Hubspot: hubspotIcon,
  Drupal: drupalIcon,
};

const ViewProject = () => {
  const { accessToken } = useAuth();
  const { projectData, loading, error, fetchProjectById } = useProjectStore();
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
  console.log("projectData", projectData);
  }, [projectData]);

  useEffect(() => {
    if (!accessToken) return;

    fetchProjectById({ accessToken, id });
  }, [id, accessToken, fetchProjectById]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="bg-[#F7F7F7] calc-h-header">
      <Navigation name={`Projects > ${id}`} />

      <div className="p-8">
        <div className="flex gap-[15px] lg:gap-0 flex-col lg:flex-row lg:items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">{projectData?.title}</h1>
            <div className="flex items-center ml-[12px]">
              <img
                src={projectTypes[projectData?.template_type]}
                alt={`${projectData?.template_type} Icon`}
                className="w-10 h-10 mr-3"
              />
            </div>
          </div>
          <div>
            <button
              onClick={() => navigate(`/projects/${id}/edit`)}
              className="px-4 py-2 bg-gradient-to-r from-[#fe6432] to-[#f8761f] rounded-[5px] justify-center items-center gap-2.5 flex"
            >
              <span className="text-white text-xs font-semibold font-['Inter'] leading-[18px] whitespace-nowrap">
                Edit
              </span>
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="flex mt-[35px] flex-col lg:flex-row">
          <div className="w-full lg:w-[70%]">
            <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] gap-[22px]">
              <div className="flex items-center mb-[22px]">
                <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                  <img src={rocketIcon} alt="Detail Icon" className="w-full" />
                </div>
                <div>
                  <h2 className="text-[18px] text-black font-semibold flex items-center">
                    Details
                  </h2>
                  <p className="text-[12px] text-[#8C8C8C]">Formed links</p>
                </div>
              </div>
              <div className="mb-[22px]">
                {projectData?.formed_links?.map((f, index) => (
                  <p
                    key={index}
                    className="text-[#8C8C8C] text-[14px] font-bold"
                  >
                    {f.flink}
                  </p>
                ))}
              </div>
              <div className="w-full">
                <h2 className="text-black text-[24px] font-bold mb-[10px]">
                  Landing Pages {projectData?.xmlFiles?.length > 1 ? `(split into ${projectData.xmlFiles.length} files)` : ''}
                </h2>
                
                {/* Display individual buttons for each XML file */}
                {projectData?.xmlFiles && projectData.xmlFiles.length > 0 ? (
                  projectData.xmlFiles.map((fileUrl, index) => (
                    <div className="flex items-center gap-[15px] mb-3" key={index}>
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = fileUrl;
                          link.setAttribute('download', '');
                          link.setAttribute('target', '_blank');
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-[#fe6432] to-[#f8761f] rounded-[5px] justify-center items-center gap-2.5 flex"
                      >
                        <span className="text-white text-xs font-semibold font-['Inter'] leading-[18px] whitespace-nowrap">
                          Click here
                        </span>
                      </button>
                      <span className="text-[#00000080] text-[16px] font-bold">
                        (Review XML file #{index + 1})
                      </span>
                    </div>
                  ))
                ) : (
                  // If no xmlFiles, show a message or fallback
                  <div className="text-gray-500">No XML files available</div>
                )}
              </div>
              <div className="mt-[20px]">
                <button className="px-4 py-2 rounded-[5px] justify-center items-center gap-2.5 flex border-[1px] border-black bg-white">
                  <a
                    href={projectData?.base_site_url}
                    className="text-black text-xs font-semibold font-['Inter'] leading-[18px] whitespace-nowrap"
                  >
                    Review at client server
                  </a>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
              <div className="flex flex-col">
                <div>
                  <h2 className="text-[18px] text-black font-semibold flex items-center">
                    Images
                  </h2>
                </div>
                {projectData?.cities?.map((c) => {
                  return <p>{c}</p>;
                })}
                <div className="flex flex-col gap-[16px] mt-[16px]">
                  {projectData?.images?.map((f) => {
                    return (
                      <div>
                        <h4 className="mb-[8px] font-bold">{f.city}</h4>
                        {f?.images?.map((i) => (
                          <div className="flex items-center">
                            <img
                              src={downloadIcon}
                              alt="Download"
                              className="w-[12px] mr-[5px]"
                            />
                            <a
                              href={i.download_url}
                              className="text-[12px] text-[#00000080] font-bold"
                            >
                              {i.name}
                            </a>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  <a
                    href={projectData?.download_all_images_url}
                    className="px-4 py-2 bg-gradient-to-r from-[#fe6432] to-[#f8761f] rounded-[5px] justify-center items-center gap-2.5 flex w-fit mt-[20px]"
                  >
                    <span className="text-white text-xs font-semibold font-['Inter'] leading-[18px] whitespace-nowrap">
                      Download all Images
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] w-full max-w-[375px] mt-[30px] lg:mt-[0] h-fit">
            <div className="flex items-center mb-[22px]">
              <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                <img src={primaryIcon} alt="Primary Icon" className="w-full" />
              </div>
              <div>
                <h2 className="text-[18px] text-black font-semibold flex items-center">
                  Primary Images
                  <Tooltip tooltip="Primary Images">
                    <img src={infoIcon} alt="Info" className="ml-1" />
                  </Tooltip>
                </h2>
              </div>
            </div>
            <div>
              <Fancybox
                options={{
                  Carousel: {
                    infinite: false,
                  },
                }}
              >
                {projectData?.files?.map((file, index) => (
                  <div
                    className="flex flex-col justify-center w-[46%]"
                    key={index}
                  >
                    <div className="primaryImage">
                      <a key={index} data-fancybox="gallery" href={file.url}>
                        <img
                          src={file.url}
                          alt={file.name}
                          className="max-w-[154px] max-h-[88px] w-full photo"
                        />
                      </a>
                    </div>
                  </div>
                ))}
              </Fancybox>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProject;
